import { exportFullTrade } from "@/components/icons/SVGIcons";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { fetchRequestWrapper } from "@/lib/utilities";
import { User } from "@auth0/auth0-react";
import { toast } from "react-toastify";

function formatTimestampForFilename(date = new Date()) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const sec = String(date.getSeconds()).padStart(2, "0");
  const ms = String(date.getMilliseconds()).padStart(3, "0");

  return `${yyyy}-${mm}-${dd}--${hh}-${min}-${sec}-${ms}`;
}

/**
 * Extracts ESIDs from AG Grid selected rows or falls back to single row ESID.
 * Used for export functionality where multiple rows can be selected.
 *
 * @param params - AG Grid GetContextMenuItemsParams containing API and node data
 * @returns Array of ESIDs from selected rows, or single ESID if no multi-selection
 */
export function extractSelectedESIDs(params: any): string[] {
  const selected = params?.api?.getSelectedRows();
  const idSelected = params?.node?.data?.esid;

  const idsToPass: string[] = [];

  if (selected.length === 0) {
    if (idSelected) {
      idsToPass.push(idSelected);
    }
  } else if (selected?.length > 0) {
    selected.forEach((trade: ExecutionStrategy) => {
      idsToPass.push(trade.esid);
    });
  }

  return idsToPass;
}

export function sellSideExport({
  selected,
  getAccessTokenSilently,
}: {
  selected: any;
  user: User | undefined;
  getAccessTokenSilently: Function;
}) {
    return {
      name: `Trade Export SellSide`,
      icon: exportFullTrade,
      action: () =>
        DownloadCSV({ getAccessTokenSilently, selected, url: "/Trade/Export" }),
    };
}

export function userSpecificExport({
  selected,
  user,
  getAccessTokenSilently,
}: {
  selected: any;
  user: User | undefined;
  getAccessTokenSilently: Function;
}) {
  if (user == undefined) {
    return null; // Prevents anything from getting added to the menu.
  }

  const customer: string | undefined =
    user?.[`https://liquiditycube.net/customer`]?.primary;

  if (customer?.includes("JBD")) {
    return {
      name: `${customer} - Trade Export`,
      icon: exportFullTrade,
      action: () =>
        DownloadCSV({ getAccessTokenSilently, selected, url: "/Trade/Export/JBD" }),
    };
  }
}

// Need to F5 the app each update to function - NOT just a code save.
export async function DownloadCSV({
  selected,
  url,
  getAccessTokenSilently,
}: {
  selected: string[];
  url: string,
  getAccessTokenSilently: Function;
}) {
  toast(
    `Generating export for legs within: ${selected.map((id) => {
      return ` ${id}`;
    })}.`,
  );

  const customerExportRequest = await fetchRequestWrapper(
    {
      url: url,
      method: "POST",
      objToSend: { ESIDs: selected },
    },
    getAccessTokenSilently,
  );

  if (customerExportRequest?.data?.length > 0) {
    const fileNameFromNetwork =
      customerExportRequest?.fileName || "Trade-Export-RP-{{TIMESTAMP}}.csv";

    function alterFileName(baseFileName: string): string {
      // const rpReplaced = baseFileName.replace("RP", customer);
      // const currentTime = new Date().toISOString();
      const timeMatchingC1Output = formatTimestampForFilename();
      const timeReplaced = baseFileName.replace(
        "{{TIMESTAMP}}",
        timeMatchingC1Output,
      );
      // let fileName = // Using a more formatted time signature.
      // `${`Trade-Export-${customer}-${returnFormattedDate(Date.now())}`}.csv` ||
      // 'Trade-Export-${customer}.csv`;
      // C1 fileName was like Trade-Export-RP-2025-12-04--12-16-02-417.csv
      return timeReplaced;
    }
    const fileName = alterFileName(fileNameFromNetwork);

    const data: [] = customerExportRequest.data;
    const csvContent = data?.map((row: string[]) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    toast.error("No data to download.");
  }
}

// NEED TO TEST:
// Single row right-click (no multi-selection) for JBD export
// Multiple rows selected for JBD export
// Verify export functionality works in both RP status conditions

// eslint-disable-next-line no-unused-vars
// COMMENTED OUT - Was working BEFORE getting moved to the helper file.
// export function downloadCSV({ params}: { params: any }) {
//   let filename = `${params.node.id}.csv` || "export.csv";

//   const currentTrade = tradeDataStore
//     .getState()
//     .tradeData.find((trade) => trade.esid == params.node.id);

//   if (!currentTrade) {
//     return;
//   }

//   let columnsAtASToExport = params.api.columnModel.columnDefs;

//   let displayedCols = params.api.getAllDisplayedColumns();
//   let displayedColsFelids = displayedCols.map(
//     (column: any) => column.colDef.field,
//   );
//   let removedFields = [
//     undefined,
//     " ",
//     "filler",
//     "Action",
//     "bid",
//     "ask",
//     "mid",
//     "indicativeMid",
//     "provider",
//   ];
//   let filteredDisplaydColFields = displayedColsFelids.filter(
//     (colField: string) => !removedFields.includes(colField),
//   );

//   // Remove unwanted rows from the export.
//   let shownColumnsAtAS = columnsAtASToExport.filter((colDef: any) => {
//     if (filteredDisplaydColFields.includes(colDef.field)) {
//       return colDef;
//     }
//   });

//   // Headers for AS
//   let csvASHeaders = shownColumnsAtAS.map(
//     (colDef: any) => `${colDef.headerName || colDef.status}`,
//   );
//   // Get the feilds that match the headers.
//   let csvASValues = [];
//   if (currentTrade) {
//     for (let index = 0; index < shownColumnsAtAS.length; index++) {
//       const currentFieldToGet = shownColumnsAtAS[index].field;
//       const fieldValue = (currentTrade as any)[currentFieldToGet];
//       csvASValues.push(fieldValue);
//     }
//   }

//   let isNotSentAndClosed = false;
//   if (params.data) {
//     if (
//       params.data.type === TradeType.NotSent &&
//       params.data.status == ExecutionStrategyStatus.NotDealt
//     ) {
//       isNotSentAndClosed = true;
//     }
//   }

//   let fullTradeData = currentTrade;

//   let colDefOptionsObj: IColDefsObject = {
//     theme,
//     expandedRowsContextCurrent,
//     setExpandedRowsContext,
//     fullTradeData,
//     getAccessTokenSilently,
//     setExpandedRowsContextMostRecentlyUpdated,
//     cryptoID,
//   };

//   let tradeDetails: ITradeDetails = {
//     // booleans for main trade conditions:
//     isOp: currentTrade.role === "OP",
//     isOutright: currentTrade.strategyType === StrategyType.Outright,
//     isRFQ: currentTrade.type === TradeType.RFQ,
//     isNotSentAndClosed: isNotSentAndClosed,
//     // booleans for alternate tables in drop down:
//     isViewingAllocaitons:
//       expandedRowsContextCurrent.find(
//         (setting) => setting.esid === currentTrade.esid,
//       )?.allocationsShowing ?? false,
//     isEditingPanel:
//       expandedRowsContextCurrent.find(
//         (setting) => setting.esid === currentTrade.esid,
//       )?.editPanelShowing ?? false,
//     // booleans for status
//     isViewingLegs:
//       expandedRowsContextCurrent.find(
//         (setting) => setting.esid === currentTrade.esid,
//       )?.legsShowing ?? false,
//     isTradeStatusNew: currentTrade.status == ExecutionStrategyStatus.New,
//     isUnwind: currentTrade.unwind == true,
//     isCancelledOPWithNoCP:
//       params.data.status == ExecutionStrategyStatus.Cancelled &&
//       params.data.role == "OP" &&
//       params.data.executionRequestCounterParties?.length == 0,
//   };

//   // ColDefs for ER table (first drop down)
//   let ERColDefs = returnColDefsForDetailTableAtActionSummary(
//     colDefOptionsObj,
//     tradeDetails,
//     theme,
//     getAccessTokenSilently,
//   );

//   let shownColumnsAtER = ERColDefs.filter((colDef: any) => {
//     if (
//       !colDef.headerCheckboxSelection &&
//       colDef.field != "" &&
//       colDef.field != "filler" &&
//       colDef.hide == false
//     ) {
//       return colDef;
//     }
//   });

//   // ColDefs for LEG table (Second drop down. Not used with Outright)
//   let LegColDefs =
//     currentTrade.role == "OP"
//       ? returnLegColDefsOP(colDefOptionsObj)
//       : returnLegColDefsRP(colDefOptionsObj);

//   let shownColumnsLeg = LegColDefs.filter((colDef: any) => {
//     if (
//       !colDef.headerCheckboxSelection &&
//       colDef.field != "" &&
//       colDef.field != "filler" &&
//       colDef.hide == false
//     ) {
//       if (colDef.field != "order") {
//         return colDef;
//       }
//     }
//   });

//   let csvERAndLegsOp: string[][] = [];
//   // Gap for a new line.
//   csvERAndLegsOp.push([]);
//   // Bulid the ER - Each loop will give a header then
//   if (currentTrade.role == "OP") {
//     currentTrade?.executionRequestCounterParties
//       ?.sort((a, b) => a.order - b.order)
//       .forEach((row: IExecutionRequestCounterParties) => {
//         csvERAndLegsOp.push([`${row.provider}`]);

//         csvERAndLegsOp.push(
//           shownColumnsAtER.map(
//             (col: any) =>
//               `${col.headerName || capitaliseFirstLetter(col.field)}`,
//           ),
//         );

//         let csvASValues = [];
//         for (let index = 0; index < shownColumnsAtER.length; index++) {
//           const currentFieldToGet = shownColumnsAtER[index].field;
//           let fieldValue = (row as any)[currentFieldToGet];
//           if (shownColumnsAtER[index].field == "order" && fieldValue == 9999) {
//             fieldValue = "";
//           } else if (shownColumnsAtER[index].field == "quotedSize") {
//             let quotedAmount = 0;
//             let totalSize = 0;
//             row.legs?.forEach((leg) => {
//               if (leg.quotedSize) {
//                 quotedAmount += leg.quotedSize;
//               }
//               if (leg.notionalLeg1) {
//                 totalSize += leg.notionalLeg1;
//               }
//             });

//             fieldValue = `${(quotedAmount / totalSize) * 100}%`;
//           }
//           if (currentTrade.strategyType == StrategyType.Outright) {
//             if (
//               // values that get brought up in the case of an outright.
//               currentFieldToGet == "bid" ||
//               currentFieldToGet == "ask" ||
//               currentFieldToGet == "mid" ||
//               currentFieldToGet == "strike" ||
//               currentFieldToGet == "direction" ||
//               currentFieldToGet == "quotedSize" ||
//               currentFieldToGet == "security" ||
//               currentFieldToGet == "settlementDate" ||
//               currentFieldToGet == "cpiLeg1" ||
//               currentFieldToGet == "cpiIndexLeg1" ||
//               currentFieldToGet == "notionalLeg1" ||
//               currentFieldToGet == "notionalLeg2" ||
//               currentFieldToGet == "book"
//             ) {
//               if (row.legs?.length > 0) {
//                 fieldValue = (row.legs[0] as any)[currentFieldToGet];
//               }
//             }
//           }

//           csvASValues.push(fieldValue);
//         }
//         csvERAndLegsOp.push(csvASValues);

//         // only do this if the trade is NOT an outright:
//         if (currentTrade.strategyType != StrategyType.Outright) {
//           csvERAndLegsOp.push([`Legs:`]);
//           csvERAndLegsOp.push(
//             shownColumnsLeg.map(
//               (col: any) =>
//                 `${col.headerName || capitaliseFirstLetter(col.field)}`,
//             ),
//           );

//           row.legs?.forEach((leg) => {
//             let currentLegValues = [];
//             for (let index = 0; index < shownColumnsLeg.length; index++) {
//               const currentFieldToGet = shownColumnsLeg[index].field;
//               const fieldValue = (leg as any)[currentFieldToGet];
//               currentLegValues.push(fieldValue);
//             }
//             csvERAndLegsOp.push(currentLegValues);
//           });
//         }
//         csvERAndLegsOp.push([]);
//       });
//   } else {
//     if (!currentTrade || !currentTrade.legs) {
//       return;
//     }
//     // Current trade will replace the ERCP's that need to be looped for the OP.
//     csvERAndLegsOp.push(
//       shownColumnsAtER.map(
//         (col: any) => `${col.headerName || capitaliseFirstLetter(col.field)}`,
//       ),
//     );

//     let csvASValues = [];
//     for (let index = 0; index < shownColumnsAtER.length; index++) {
//       const currentFieldToGet = shownColumnsAtER[index].field;
//       let fieldValue = (currentTrade as any)[currentFieldToGet];
//       if (shownColumnsAtER[index].field == "order" && fieldValue == 9999) {
//         fieldValue = "";
//       } else if (shownColumnsAtER[index].field == "quotedSize") {
//         let quotedAmount = 0;
//         let totalSize = 0;
//         currentTrade.legs?.forEach((leg) => {
//           if (leg.quotedSize) {
//             quotedAmount += leg.quotedSize;
//           }
//           if (leg.notionalLeg1) {
//             totalSize += leg.notionalLeg1;
//           }
//         });

//         fieldValue = `${(quotedAmount / totalSize) * 100}%`;
//       }
//       if (currentTrade.strategyType == StrategyType.Outright) {
//         if (
//           // values that get brought up in the case of an outright.
//           currentFieldToGet == "bid" ||
//           currentFieldToGet == "ask" ||
//           currentFieldToGet == "mid" ||
//           currentFieldToGet == "strike" ||
//           currentFieldToGet == "direction" ||
//           currentFieldToGet == "quotedSize" ||
//           currentFieldToGet == "security" ||
//           currentFieldToGet == "settlementDate" ||
//           currentFieldToGet == "cpiLeg1" ||
//           currentFieldToGet == "cpiIndexLeg1" ||
//           currentFieldToGet == "notionalLeg1" ||
//           currentFieldToGet == "notionalLeg2" ||
//           currentFieldToGet == "book"
//         ) {
//           if (currentTrade.legs?.length > 0) {
//             if (currentFieldToGet == "security") {
//               fieldValue = (currentTrade.legs[0] as any).securityName;
//             } else {
//               fieldValue = (currentTrade.legs[0] as any)[currentFieldToGet];
//             }
//           }
//         }
//       }

//       csvASValues.push(fieldValue);
//     }
//     csvERAndLegsOp.push(csvASValues);

//     // only do this if the trade is NOT an outright:
//     if (currentTrade.strategyType != StrategyType.Outright) {
//       csvERAndLegsOp.push([`Legs:`]);
//       csvERAndLegsOp.push(
//         shownColumnsLeg.map(
//           (col: any) => `${col.headerName || capitaliseFirstLetter(col.field)}`,
//         ),
//       );

//       currentTrade.legs?.forEach((leg) => {
//         let currentLegValues = [];
//         for (let index = 0; index < shownColumnsLeg.length; index++) {
//           const currentFieldToGet = shownColumnsLeg[index].field;
//           const fieldValue = (leg as any)[currentFieldToGet];
//           currentLegValues.push(fieldValue);
//         }
//         csvERAndLegsOp.push(currentLegValues);
//       });
//     }
//     csvERAndLegsOp.push([]);
//   }

//   const data: string[][] = [[...csvASHeaders], [...csvASValues]];
//   csvERAndLegsOp.forEach((arr) => data.push(arr));

//   const csvContent = data.map((row) => row.join(",")).join("\n");

//   const blob = new Blob([csvContent], { type: "" });
//   const url = URL.createObjectURL(blob);

//   const link = document.createElement("a");
//   link.setAttribute("href", url);
//   link.setAttribute("download", filename);
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }
